import { cva, type VariantProps } from "class-variance-authority";
import { useId } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { cn } from "../utils";
import { FormError } from "./form-error";
import { Switch } from "./switch";

/** Props Interface */
export interface FormSwitchProps<T extends FieldValues> extends VariantProps<typeof switchInput> {
	name: Path<T>; // Ensures name is a valid field from T
	control: Control<T>;
	children: React.ReactNode;
	isDisabled?: boolean;
	"data-cy"?: string;
}

export function FormSwitch<T extends FieldValues>({
	name,
	control,
	children,
	isDisabled,
	width,
	"data-cy": cyId,
	...props
}: FormSwitchProps<T>) {
	/** Hooks */
	const id = useId();

	const switchInputClasses = switchInput({ width });

	/** Render */
	return (
		<Controller
			name={name}
			control={control}
			render={({ field, formState }) => (
				<div className="flex flex-col gap-1 w-full">
					<div className={cn("flex items-center gap-2 w-full cursor-pointer", switchInputClasses)}>
						<Switch
							id={`${id}-${name}`}
							isDisabled={isDisabled || false}
							checked={field.value}
							onCheckedChange={field.onChange}
							data-cy={cyId}
							{...props}
						/>
						<label
							className="text-color text-sm cursor-pointer select-none"
							htmlFor={`${id}-${name}`}
						>
							{children}
						</label>
					</div>
					<FormError
						errors={formState.errors}
						name={name}
					/>
				</div>
			)}
		/>
	);
}

const switchInput = cva([], {
	variants: {
		width: {
			default: ["flex-row"],
			full: ["flex-row-reverse justify-between"],
		},
	},
	defaultVariants: {
		width: "default",
	},
});
